import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { CommonService } from './common.service';
import { AddExpectFundDto } from '../models/expectFundRequestDTO';
import { AddPaymentRequestDto } from '../models/paymentRequestDto';
import { RequestDocAttachmentDto, RequestDocAttachmentResponse } from '../models/requestDocAttachment';
import { PaymentResponseDto } from '../models/paymentResponseDto';
import { ExpectFundResponseDto } from '../models/expectResponseDto';
import { checkLock } from '../models/checkLock';
import { TransferFundResponseDto } from '../models/transferFundResponseDto';
import { AddTransferFundDto } from '../models/transferFundRequestDto';
import { Currency } from '../models/currency';
import { Authoriser } from '../models/authoriser';
import { Jurisdiction } from '../models/jurisdiction';
import { PurchaseCodeDto } from '../models/PurchaseCodeLookup';
import { ApprovalDetails } from '../models/ApprovalDetails';
import { AddJournalRequestDto } from '../models/journalRequestDto';
import { JournalResponseDto } from '../models/journalResponseDto';
import { RequestWorkflowDto } from '../models/request-stage';
import { HttpClient } from '@angular/common/http';
import { RequestType } from '../../enums/request-type.enum';

@Injectable({
  providedIn: 'root'
})
export class RequestsService {
    constructor(private dataService: DataService, private http: HttpClient, private commonService: CommonService) {
    }

    async addExpectFund(data: AddExpectFundDto): Promise<ExpectFundResponseDto> {
        return await this.dataService.post<ExpectFundResponseDto>('api/requests/expect-fund/add', data);
    }

    async addPaymentRequest(data: AddPaymentRequestDto): Promise<PaymentResponseDto> {
        return await this.dataService.post<PaymentResponseDto>('api/requests/payment-request/add', data);
    }

    async addTransferFundRequest(data: AddTransferFundDto): Promise<TransferFundResponseDto> {
        return await this.dataService.post<TransferFundResponseDto>('api/requests/transfer-funds/add', data);
    }
    async addJournalRequest(data: AddJournalRequestDto): Promise<JournalResponseDto> {
        return await this.dataService.post<JournalResponseDto>('api/requests/journal-request/add', data);
    }
    async addRequestDocument(data: RequestDocAttachmentResponse): Promise<string> {
        return await this.dataService.post<string>('api/request-doc/add', data);
    }

    /** Removes a document from one of the current user's unsubmitted drafts. */
    async deleteRequestDocument(documentId: number): Promise<void> {
        await this.dataService.delete<void>('api/request-doc', documentId);
    }

    async getRequestDocumentList(requestId: number, requestType: string): Promise<RequestDocAttachmentResponse> {
        return await this.dataService.get<RequestDocAttachmentResponse>(`api/request-doc?id=${requestId}&request=${requestType}`);
    }

    async getRequestDocument(documentId: number): Promise<RequestDocAttachmentResponse> {
        return await this.dataService.get<RequestDocAttachmentResponse>(`api/request-doc/download/${documentId}`);
    }

    async getPaymentRequestList(): Promise<PaymentResponseDto[]> {
        return await this.dataService.get<PaymentResponseDto[]>('api/requests/payment-request');
    }

    async getPaymentRequestById(id: number): Promise<PaymentResponseDto> {
        return await this.dataService.get<PaymentResponseDto>(`api/requests/payment-request/${id}`);
    }

    async GetExpectFundBasedOnId(id: number): Promise<ExpectFundResponseDto> {
        return await this.dataService.get<ExpectFundResponseDto>(`api/requests/expect-fund/${id}`);
    }

    async GetJournalRequestBasedOnId(id: number): Promise<JournalResponseDto> {
        return await this.dataService.get<JournalResponseDto>(`api/requests/journal-request/${id}`);
    }

    async getExpectFundList(): Promise<ExpectFundResponseDto[]> {
        return await this.dataService.get<ExpectFundResponseDto[]>('api/requests/expect-fund');
    }

    async getTransferFundsList(isRequestCreation: boolean = false): Promise<TransferFundResponseDto[]> {
        return await this.dataService.get<TransferFundResponseDto[]>(`api/requests/transfer-funds?isRequestCreation=${isRequestCreation}`);
    }

    async GetTransferFundbyId(id: number): Promise<TransferFundResponseDto> {
        return await this.dataService.get<TransferFundResponseDto>(`api/requests/transfer-funds/${id}`);
    }

    async getJournalRequestList(): Promise<JournalResponseDto[]> {
        return await this.dataService.get<JournalResponseDto[]>('api/requests/journal-request');
    }

    async returnPaymentRequest(id: number, stageId: number, returnReason: string, isRPAReversalRequested: boolean, isRejectionRequested: boolean): Promise<string> {
        return await this.dataService.put(`api/requests/payment-request/return?id=${id}&stageId=${stageId}&isRPAReversalRequested=${isRPAReversalRequested}&isRejectionRequested=${isRejectionRequested}`,returnReason);
    }

    async deletePaymentRequest(id: number): Promise<string> {
        return await this.dataService.put(`api/requests/payment-request/delete?id=${id}`, {});
    }

    async returnExpectFund(id: number, stageId: number, returnReason: string, isRPAReversalRequested: boolean): Promise<string> {
        return await this.dataService.put(`api/requests/expect-fund/return?id=${id}&stageId=${stageId}&isRPAReversalRequested=${isRPAReversalRequested}`, returnReason);
    }

    async deleteExpectFund(id: number): Promise<string> {
        return await this.dataService.put(`api/requests/expect-fund/delete?id=${id}`, {});
    }

    async returnTransferRequest(id: number, stageId: number, returnReason: string, isRPAReversalRequested: boolean): Promise<string> {
        return await this.dataService.put(`api/requests/transfer-funds/return?id=${id}&stageId=${stageId}&isRPAReversalRequested=${isRPAReversalRequested}`, returnReason);
    }

    async deleteTransferFund(id: number): Promise<string> {
        return await this.dataService.put(`api/requests/transfer-funds/delete?id=${id}`, {});
    }
    async returnJournalRequest(id: number, stageId: number, returnReason: string, isRPAReversalRequested: boolean, isRejectionRequested: boolean): Promise<string> {
        return await this.dataService.put(`api/requests/journal-request/return?id=${id}&stageId=${stageId}&isRPAReversalRequested=${isRPAReversalRequested}&isRejectionRequested=${isRejectionRequested}`, returnReason);
    }
    async deleteJournalRequest(id: number): Promise<string> {
        return await this.dataService.put(`api/requests/journal-request/delete?id=${id}`, {});
    }

    async approveRequest(id: number, requestType: string, stageId: number, approvalDetails?: ApprovalDetails): Promise<string> {
        return await this.dataService.post<string>(`api/requests/approve?id=${id}&request=${requestType}&stageId=${stageId}`, approvalDetails ?? null);
    }

    async checkLockStatus(id: number, request: string): Promise<checkLock> {
        return await this.dataService.get<checkLock>(`api/requests/check-lock?id=${id}&request=${request}`);
    }

    async requestLockorUnLock(id: number, request: string): Promise<boolean> {
        return await this.dataService.post<boolean>(`api/requests/lock?id=${id}&request=${request}`, {});
    }

    async validateJurisdiction(orgKey: string): Promise<Jurisdiction> {
        return await this.dataService.get<Jurisdiction>(`api/users/auth-jurisdiction/${orgKey}`);
    }

    async getPartnersList(): Promise<Authoriser[]> {
        return await this.dataService.get<Authoriser[]>('api/requests/partners-list');
    }

    async getAllCurrencies(): Promise<Currency[]> {
        return await this.dataService.get<Currency[]>('api/requests/currencies');
    }

    getPurchaseCodeLookup(): Promise<PurchaseCodeDto[]> {
        return this.dataService.get<PurchaseCodeDto[]>('api/Requests/purchasecodes');
    }

    getAllStages(): Promise<RequestWorkflowDto[]> {
        return this.dataService.get<RequestWorkflowDto[]>(`api/Requests/stages`);
    }

    getStagesByRequestType(requestTypeId: number): Promise<RequestWorkflowDto[]> {
        return this.dataService.get<RequestWorkflowDto[]>(
            `api/Requests/stages/${requestTypeId}`
        );
    }

    downloadRequestPdf(id: number, requestType: RequestType) {
        this.http.get(
            `/api/requests/${id}/pdf?requestType=${requestType}`,
            { responseType: 'blob' }
        ).subscribe(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${requestType}_Request_${id}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }

    downloadAllPdfsAsZip(items: { requestId: number; requestType: string }[], filePrefix: string = 'Request_PDFs'): Promise<void> {
        return this.http.post(
            '/api/requests/pdf/bulk-zip',
            items,
            { responseType: 'blob' }
        ).toPromise().then(blob => {
            if (!blob) return;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            a.download = `${filePrefix}_${timestamp}.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }

    async getActionstepUrl(): Promise<string> {
        return await this.dataService.get<string>('api/requests/actionstep-url');
    }

    async getActionStepOverviewUrl(): Promise<string> {
        return await this.dataService.get<string>('api/requests/actionstep-overview-url');
    }

    unlockAllByUser(): void {
        const encryptedToken = localStorage.getItem('token');
        if (!encryptedToken) return;

        const token = this.commonService.decrypt(encryptedToken);
        if (!token) return;

        fetch(`${window.location.origin}/api/requests/unlock-all`, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
        });
    }
}
